/**
 * Optimistic Concurrency Control for Sheets rows.
 *
 * Per design.md「Optimistic Concurrency Control 用 per-row rev 欄位」:
 *
 * 1. read header row + the target row by id
 * 2. compare server `rev` vs `expectedRev`
 * 3. if mismatch → append `occ_conflict` notification + throw
 * 4. if match → write back row with `rev = old + 1, updated_at = now()`
 *
 * `expectedRev` is required (not defaulted) — a default of 0 would silently
 * disable OCC for new updates that forget to thread the rev through.
 *
 * Object-arg API: `updateRow`, `softDeleteRow`, `restoreRow` all take a
 * single options object. Five of the six fields used to be positional
 * `string`s — a refactor that swapped any two would compile and silently
 * 404. With named fields a swap is impossible; with branded ids
 * (`SpreadsheetId` / `TransactionId` / etc.) even reusing one id field for
 * the wrong slot is a compile error.
 */

import type { SheetsClient } from './client';
import type { TabName } from './schema';
import { TABS, tabRange } from './schema';
import { rowToObject, objectToRow, tabRowRange, nowIso, parseInt10 } from './row-utils';
import { notificationsRepo } from './repositories/notifications';
import type { SpreadsheetId } from './branded';

const OCC_TABS = ['transactions', 'books', 'categories', 'recurring'] as const;
export type OccTab = (typeof OCC_TABS)[number];

export class OccConflictError extends Error {
  readonly id: string;
  readonly serverRev: number;
  readonly localRev: number;
  constructor(id: string, serverRev: number, localRev: number) {
    super(`OCC conflict on ${id}: server rev=${serverRev}, local rev=${localRev}`);
    this.name = 'OccConflictError';
    this.id = id;
    this.serverRev = serverRev;
    this.localRev = localRev;
  }
}

export class RowNotFoundError extends Error {
  constructor(public readonly tab: string, public readonly id: string) {
    super(`Row not found: ${tab}/${id}`);
    this.name = 'RowNotFoundError';
  }
}

/**
 * Thrown when `expectedRev` is not a non-negative integer >= 1.
 *
 * `rev=0` is reserved for "uninitialised" rows (pre-Section 5 schema). Any
 * caller passing `0`, negative numbers, `NaN`, or `Infinity` is leaking a
 * parse failure or skipping rev tracking entirely — both are bugs we want
 * to surface loudly rather than silently overwrite with a stale rev.
 */
export class InvalidRevError extends Error {
  readonly value: unknown;
  constructor(value: unknown) {
    super(
      `Invalid expectedRev: ${String(value)} — must be an integer >= 1`,
    );
    this.name = 'InvalidRevError';
    this.value = value;
  }
}

/**
 * Thrown when `updateRow` patch attempts to write an immutable cell.
 *
 * Currently: `id`, `created_at`, `deleted_at`. The first two are protected
 * by the row identity / history; `deleted_at` is protected because clearing
 * it via the generic update path would let any caller un-delete a soft-
 * deleted row. Use `restoreRow` for the explicit un-delete path.
 */
export class ImmutableFieldError extends Error {
  readonly field: string;
  constructor(field: string) {
    super(`Cannot modify immutable field via update: ${field}`);
    this.name = 'ImmutableFieldError';
    this.field = field;
  }
}

/** Fields that `updateRow` MUST refuse to write through the patch path.
 *  `id` and `created_at` are guarded by the existing identity logic; we add
 *  `deleted_at` here so soft-delete can only be cleared via `restoreRow`. */
export const IMMUTABLE_PATCH_FIELDS: ReadonlySet<string> = new Set([
  'deleted_at',
]);

interface FoundRow {
  rowNumber: number; // 1-indexed Sheets row number (header is row 1)
  values: Record<string, string>;
}

async function findRowById(
  client: SheetsClient,
  spreadsheetId: SpreadsheetId | string,
  tab: TabName,
  id: string,
): Promise<FoundRow | null> {
  const headers = TABS[tab].headers;
  const fullRange = tabRange(tab, { startRow: 2 });
  const [headerResp, dataResp] = await Promise.all([
    client.valuesGet(spreadsheetId, `${tab}!A1:${columnLetterOfHeader(tab)}1`),
    client.valuesGet(spreadsheetId, fullRange),
  ]);

  // sanity check headers exist; if absent treat as empty.
  void headerResp;

  const idIdx = headers.indexOf('id');
  if (idIdx === -1) throw new Error(`tab ${tab} has no 'id' column`);

  const rows = (dataResp.values ?? []) as string[][];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i] ?? [];
    if ((r[idIdx] ?? '') === id) {
      return { rowNumber: i + 2, values: rowToObject(headers, r) };
    }
  }
  return null;
}

function columnLetterOfHeader(tab: TabName): string {
  const headers = TABS[tab].headers;
  const idx = headers.length - 1;
  let n = idx;
  let s = '';
  while (n >= 0) {
    s = String.fromCharCode((n % 26) + 65) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

// ---------------------------------------------------------------------------
// Public object-arg APIs
// ---------------------------------------------------------------------------

/**
 * Args for `updateRow`. Every field is named — a refactor cannot swap two
 * `string`-shaped values silently. Branded ids (`SpreadsheetId`) make even
 * accidental reuse of an id field a compile error.
 */
export interface UpdateRowArgs {
  client: SheetsClient;
  spreadsheetId: SpreadsheetId | string;
  tab: OccTab;
  id: string;
  patch: Record<string, string>;
  /** REQUIRED — see `InvalidRevError` JSDoc. No default is exposed. */
  expectedRev: number;
}

export interface SoftDeleteRowArgs {
  client: SheetsClient;
  spreadsheetId: SpreadsheetId | string;
  tab: OccTab;
  id: string;
  expectedRev: number;
}

export type RestoreRowArgs = SoftDeleteRowArgs;

/**
 * Read-modify-write a row with OCC.
 *
 * @param args.expectedRev REQUIRED — the rev the caller saw when they
 *                         fetched the row. MUST be an integer >= 1. New
 *                         rows start at rev=1 (see
 *                         `_base.ts:newRowDefaults`). Mismatch surfaces as
 *                         `OccConflictError` and a queued `occ_conflict`
 *                         notification.
 *
 * @throws InvalidRevError when `expectedRev` is NaN/Infinity/negative/0/non-int.
 * @throws ImmutableFieldError when `patch` touches an immutable cell.
 * @throws OccConflictError on rev mismatch.
 */
export async function updateRow(
  args: UpdateRowArgs,
): Promise<{ rev: number; updated_at: string }> {
  return updateRowInternal(args, {});
}

interface UpdateInternalOptions {
  /** When true, allow the caller to write to immutable fields (used by
   *  the soft-delete and restore paths). */
  bypassImmutable?: boolean;
}

async function updateRowInternal(
  args: UpdateRowArgs,
  opts: UpdateInternalOptions,
): Promise<{ rev: number; updated_at: string }> {
  const { client, spreadsheetId, tab, id, patch, expectedRev } = args;
  if (!Number.isInteger(expectedRev) || expectedRev < 1) {
    throw new InvalidRevError(expectedRev);
  }

  if (!opts.bypassImmutable) {
    for (const k of Object.keys(patch)) {
      if (IMMUTABLE_PATCH_FIELDS.has(k)) {
        throw new ImmutableFieldError(k);
      }
    }
  }

  const found = await findRowById(client, spreadsheetId, tab, id);
  if (!found) throw new RowNotFoundError(tab, id);

  const headers = TABS[tab].headers;
  const serverRev = parseInt10(found.values['rev'], 0);
  if (serverRev !== expectedRev) {
    await notificationsRepo.append(
      { client, spreadsheetId },
      {
        kind: 'occ_conflict',
        payload: {
          tx_id: id,
          server_rev: serverRev,
          local_rev: expectedRev,
        },
      },
    );
    throw new OccConflictError(id, serverRev, expectedRev);
  }

  const updated_at = nowIso();
  const newRev = serverRev + 1;
  const merged: Record<string, string> = {
    ...found.values,
    ...patch,
    rev: String(newRev),
    updated_at,
  };
  // protect immutables that survive even the bypass path
  merged['id'] = found.values['id'] ?? id;
  if (found.values['created_at']) merged['created_at'] = found.values['created_at'];

  const rowValues = objectToRow(headers, merged);
  await client.valuesUpdate(spreadsheetId, tabRowRange(tab, found.rowNumber), [rowValues]);
  return { rev: newRev, updated_at };
}

/**
 * Soft-delete via setting `deleted_at = now()`. We deliberately do NOT clear
 * other cells — preserving row index stability and history.
 *
 * Bypasses the `deleted_at` immutable-field block (the block exists exactly
 * to prevent the public update path from forging this).
 */
export async function softDeleteRow(
  args: SoftDeleteRowArgs,
): Promise<{ rev: number; deleted_at: string }> {
  const deleted_at = nowIso();
  const result = await updateRowInternal(
    {
      client: args.client,
      spreadsheetId: args.spreadsheetId,
      tab: args.tab,
      id: args.id,
      patch: { deleted_at },
      expectedRev: args.expectedRev,
    },
    { bypassImmutable: true },
  );
  return { rev: result.rev, deleted_at };
}

/**
 * Explicit un-delete: clears `deleted_at` back to '' and bumps rev.
 *
 * Mirror of `softDeleteRow`. Provided as a separate, named code path
 * because clearing `deleted_at` via the generic update patch path is
 * blocked (`ImmutableFieldError`). Callers wanting un-delete must use
 * this entry point.
 */
export async function restoreRow(
  args: RestoreRowArgs,
): Promise<{ rev: number; deleted_at: '' }> {
  await updateRowInternal(
    {
      client: args.client,
      spreadsheetId: args.spreadsheetId,
      tab: args.tab,
      id: args.id,
      patch: { deleted_at: '' },
      expectedRev: args.expectedRev,
    },
    { bypassImmutable: true },
  );
  // Re-read isn't necessary; we know the new rev = expectedRev + 1.
  return { rev: args.expectedRev + 1, deleted_at: '' };
}
